import {action, makeObservable, observable, runInAction} from "mobx";
import EventSummaryItem from "./models/eventSummaryItem";
import {IEventService} from "../../../services/eventService";
import EventDetails, {
  CustomLocationConstant,
  VirtualEventConstant
} from "./models/eventDetails";
import EventCategory from "./models/eventCategory";
import {KeyValue} from "../../observables/keyValue";
import {FileCreated, IFileService} from "../../../services/fileService";
import {ResourceCreatedOrUpdated} from "../../../services/resourceCreatedOrUpdated";
import EventSharedLocation from "./models/eventSharedLocation";
import {isWebLink} from "../../../utils/stringUtils";
import {IUserService} from "../../../services/userService";
import {IVillageScheduleService} from "../../../services/villageScheduleService";
import CalendarSchedule from "./models/calendarSchedule";
import EventBooking from "./models/eventBooking";
import {translationProvider} from "../../../services/translationProvider";
import {
  allVillageKeyValue,
  allVillagesInAU,
  allVillagesInNZ,
  recurrenceWeekdays
} from "../../../shared/constants";
import {AuthUser} from "../../../utils/auth/user";
import {EventStatus} from "../../../shared/enums/eventStatus";
import {TimeZones} from "../../../shared/enums/timeZones";
import {utcToZonedTime} from "date-fns-tz";
import {EventRecurrenceDetails} from "./models/eventRecurrenceDetails";
import {DateObservable} from "../../observables/dateObservable";
import {UpdateRecurringEventOption} from "../../../shared/enums/updateRecurringEventOption";
import {addDays} from "date-fns";
import {EventCalendarType} from "../../../shared/enums/eventCalendarType";
import EventOccurrenceInformation from "./models/eventOccurrenceInformation";
import {VillageCalendar} from "../../../shared/interfaces/villageCalendar";
import {filterVillagesByServicedApartmentAudience} from "../../../shared/interfaces/village";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import {IVillageCalendarsDomainStore} from "./villageCalendarsDomainStore";

export interface IEventsDomainStore {
  invalidateCalendarRequests: () => void;
  eventsList: EventSummaryItem[];
  getAllEvents: (
    startDate: Date,
    endDate: Date,
    calendarName: string,
    includeFullDetails?: boolean
  ) => Promise<void>;
  getEventsForExport: (
    startDate: Date,
    endDate: Date,
    calendarName: string,
    includeFullDetails?: boolean
  ) => Promise<EventSummaryItem[]>;
  setEditableCategories: () => void;
  setEditableLocations: () => void;
  getEventCategories: (calendarType: EventCalendarType) => Promise<void>;
  getEventLocations: () => Promise<void>;
  getVillages: (calendarName?: string) => Promise<void>;
  resetEditableEvent: () => void;
  createEvent: () => Promise<string>;
  editableEvent: EventDetails;
  eventCategories: EventCategory[];
  sharedLocations: EventSharedLocation[];
  loadEditableEvent: (
    id: string,
    reset?: boolean,
    villageId?: string
  ) => Promise<void>;
  editEvent: () => Promise<any>;
  deleteEvent: () => Promise<any>;
  deleteEventSeries: () => Promise<any>;
  cancelPostponeEvent: (eventStatus: EventStatus) => Promise<any>;
  getEventImage: (eventDetails?: EventDetails) => Promise<void>;
  selectedVillageId?: string;
  startDate?: Date;
  getCalendarSchedule: () => Promise<CalendarSchedule>;
  setIsMultiVillageEvent: () => void;

  /**
   * Loads available event occurrences.
   * */
  loadEventOccurrences: (
    withBookingsOnly?: boolean,
    eventDetails?: EventDetails
  ) => Promise<void>;

  /**
   * An array of confirmed event bookings for an event
   */
  confirmedEventBookingsList: EventBooking[];
  /**
   * An array of waitlisted event bookings for an event
   */
  waitlistEventBookingsList: EventBooking[];
  /**
   * An array of cancelled event bookings for an event
   */
  cancelledEventBookingsList: EventBooking[];
  /**
   * Gets a list of bookings for an event.
   */
  getEventBookings: (eventId: string, status?: string) => Promise<void>;
  /**
   * Updates an event booking.
   */
  updateEventBooking: (eventBooking: EventBooking) => Promise<any>;
  /**
   * Update event bookings.
   */
  updateMultipleEventBookings: (eventBookings: EventBooking[]) => Promise<any>;
  /**
   * Creates an event booking.
   */
  createEventBooking: (
    eventId: string,
    eventBooking: EventBooking
  ) => Promise<any>;
  /**
   * Resets/refreshes the event bookings list.
   */
  resetEventBookings: () => void;
  /**
   * Get the time zone of the selected village.
   */
  getTimeZone: () => string;

  /**
   * Get the occurrenceDate when viewing recurring event.
   */
  setOccurrenceDate: (occurrenceDate?: string | undefined | null) => void;

  /**
   * Setter for recurring event update options
   */
  setEventUpdateOption: (option?: UpdateRecurringEventOption) => void;

  /**
   * The date of an event series occurrence
   */
  occurrenceDate?: string;

  /**
   * UpdateRecurringEventOption
   */
  eventUpdateOption?: UpdateRecurringEventOption;

  /**
   * Information about Event Occurrences, including date and booking details
   */
  eventOccurrenceInfos: EventOccurrenceInformation[];

  /**
   * Information about occurrences that are booked, including date and booking details
   */
  bookedEventOccurrenceInfos: EventOccurrenceInformation[];

  /**
   * Next release period start date
   */
  getNextReleasePeriodStartDate: () => Date | undefined;

  /**
   * Current time in village timezone
   */
  getCurrentTime: () => Date;

  /**
   * Checks whether non-cosmetic changes can be made to an event series.
   * In order to make non-cosmetic changes, the event should not end in the current released month.
   */
  canMakeNonCosmeticChanges: () => boolean;

  /**
   * Get the code of the selected village.
   */
  getVillageCode: () => string;

  /**
   * Get calendar by selected calendar name.
   */
  getCalendarByName: (calendarName: string) => VillageCalendar | undefined;

  /**
   * Load categorised events.
   */
  loadCategorisedEvents: (
    calendarType: EventCalendarType,
    startDate: Date,
    endDate: Date,
    villageId: string,
    personType: string
  ) => Promise<void>;

  /**
   * Get categorised events
   */
  categorisedEvents: EventCategory[];
}

export class EventsDomainStore implements IEventsDomainStore {
  private readonly eventService: IEventService;
  private readonly fileService: IFileService;
  private readonly userService: IUserService;
  private readonly villageScheduleService: IVillageScheduleService;
  private readonly villageCalendarsDomainStore?: IVillageCalendarsDomainStore;
  private editableEventLoadId = 0;
  private eventsListRequestId = 0;
  private categorisedEventsRequestId = 0;

  multiSelectVillages: KeyValue[] = [];
  eventsList: EventSummaryItem[] = [];
  eventCategories: EventCategory[] = [];
  sharedLocations: EventSharedLocation[] = [];
  editableEvent: EventDetails;
  confirmedEventBookingsList: EventBooking[] = [];
  waitlistEventBookingsList: EventBooking[] = [];
  cancelledEventBookingsList: EventBooking[] = [];
  occurrenceDate?: string;
  eventUpdateOption?: UpdateRecurringEventOption;
  eventOccurrenceInfos: EventOccurrenceInformation[] = [];
  bookedEventOccurrenceInfos: EventOccurrenceInformation[] = [];
  nextReleasePeriodStartDate?: Date;
  categorisedEvents: EventCategory[] = [];

  constructor(
    eventService: IEventService,
    fileService: IFileService,
    userService: IUserService,
    villageScheduleService: IVillageScheduleService,
    villageCalendarsDomainStore?: IVillageCalendarsDomainStore
  ) {
    makeObservable(this, {
      eventsList: observable,
      editableEvent: observable,
      confirmedEventBookingsList: observable,
      waitlistEventBookingsList: observable,
      cancelledEventBookingsList: observable,
      getAllEvents: action,
      getEventBookings: action,
      eventOccurrenceInfos: observable,
      bookedEventOccurrenceInfos: observable,
      categorisedEvents: observable,
      loadCategorisedEvents: action
    });
    this.eventService = eventService;
    this.fileService = fileService;
    this.userService = userService;
    this.villageScheduleService = villageScheduleService;
    this.villageCalendarsDomainStore = villageCalendarsDomainStore;
    this.editableEvent = new EventDetails(TimeZones.Auckland);
  }

  startDate?: Date | undefined;

  getCalendarSchedule = async (): Promise<CalendarSchedule> => {
    const villageId = this.userService.getSelectedVillage()?.id;
    if (!villageId) {
      throw new Error("Village not selected");
    }

    const schedule = await this.villageScheduleService.getVillageSchedule(
      villageId!
    );

    const timeZone = this.getTimeZone();
    return new CalendarSchedule(
      utcToZonedTime(new Date(schedule.calendarStartDate), timeZone),
      utcToZonedTime(new Date(schedule.calendarEndDate), timeZone),
      utcToZonedTime(new Date(schedule.calendarReleaseDate), timeZone)
    );
  };

  getAllEvents = async (
    startDate: Date,
    endDate: Date,
    calendarName: string,
    includeFullDetails?: boolean
  ) => {
    const requestId = ++this.eventsListRequestId;
    const calendar = this.getCalendarByName(calendarName);
    const data = await this.eventService.getAllEvents(
      startDate,
      endDate,
      this.userService.getSelectedVillage()?.id,
      await this.resolvePersonTypeFilter(
        calendarName,
        calendar?.personType,
        calendar?.personTypeIds
      ),
      this.resolveIncludeFullDetails(includeFullDetails)
    );

    // When updating observables in async, we need to mark them as action. Otherwise, warnings will be shown in the browser's developer console.
    // https://mobx.js.org/actions.html#asynchronous-actions
    runInAction(() => {
      if (requestId === this.eventsListRequestId) {
        this.eventsList = data.map((d: any) =>
          this.mapEventToEventSummaryItem(d)
        );
      }
    });
  };

  getEventsForExport = async (
    startDate: Date,
    endDate: Date,
    calendarName: string,
    includeFullDetails?: boolean
  ) => {
    const calendar = this.getCalendarByName(calendarName);
    const data = await this.eventService.getAllEvents(
      startDate,
      endDate,
      this.userService.getSelectedVillage()?.id,
      await this.resolvePersonTypeFilter(
        calendarName,
        calendar?.personType,
        calendar?.personTypeIds
      ),
      this.resolveIncludeFullDetails(includeFullDetails)
    );

    return data.map((d: any) => this.mapEventToEventSummaryItem(d));
  };

  /**
   * Multi-Village calendars always need full event details (villages, person types)
   * fetched from the CMS, regardless of what the caller requested, since the admin UI
   * can't otherwise tell which villages/person types a multi-village event applies to.
   */
  private resolveIncludeFullDetails = (
    includeFullDetails?: boolean
  ): boolean | undefined =>
    includeFullDetails || this.userService.isMultiVillageOptionSelected();

  /**
   * Resolves the personType query value used by the events fetch.
   *
   * Prefers the CMS-first multi list (`personTypeIds`) surfaced through the
   * admin BFF `/api/villagecalendars` endpoint when the selected village
   * calendar is mapped to multiple person types (e.g. IA + SA for the SA
   * onboarding POC). Falls back to the single Integration API
   * `VillageCalendar.personType` so legacy single-audience calendars keep
   * working. Without this the Integration API returns `personType=0` for
   * multi-audience CMS calendars, which yields zero events.
   *
   * Between those two, also honors `personTypeIds` declared directly on the
   * calendar object itself (`ownPersonTypeIds`). This covers calendars that
   * have no real `villageId` to match against the CMS store — e.g. the
   * synthetic Multi-Village calendars in `MultiVillageHelper.multiVillageVillage`,
   * which can target multiple person types (e.g. "Independent and Serviced
   * Apartment Residents") but never have a CMS-store match.
   */
  private resolvePersonTypeFilter = async (
    calendarName: string,
    fallbackPersonType: string | undefined,
    ownPersonTypeIds?: (string | null | undefined)[] | null
  ): Promise<string | undefined> => {
    const selectedVillageId = this.userService.getSelectedVillage()?.id;
    const cmsStore = this.villageCalendarsDomainStore;
    if (cmsStore && selectedVillageId) {
      // Lazily populate the CMS calendar → personTypeIds lookup so we can
      // resolve multi-audience calendars even when the user landed on the
      // events page directly (createEditEvent / catalogue paths refresh
      // it eagerly, but the calendar view previously never did).
      try {
        if (!this.userService.isMultiVillageOptionSelected()) {
          await cmsStore.getVillageCalendars(
            undefined,
            String(selectedVillageId)
          );
        }
      } catch {
        // Non-fatal: fall back to the Integration API single value below.
      }
      const cmsMatch = cmsStore.villageCalendars.find(
        (c) =>
          String(c.villageId) === String(selectedVillageId) &&
          c.name === calendarName
      );
      const cmsIds = cmsMatch?.personTypeIds?.filter(
        (id): id is string => !!id
      );
      if (cmsIds && cmsIds.length > 0) {
        return cmsIds.join(",");
      }
    }
    const ownIds = ownPersonTypeIds?.filter((id): id is string => !!id);
    if (ownIds && ownIds.length > 0) {
      return ownIds.join(",");
    }
    return fallbackPersonType;
  };

  getEventLocations = async () => {
    let data = await this.eventService.getAllEventSharedLocations();

    this.sharedLocations = data
      .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
      .map((d: any) => new EventSharedLocation(d.id, d.name));
    this.setEditableLocations();
  };

  getEventCategories = async (calendarType: EventCalendarType) => {
    let data = await this.eventService.getAllEventCategories(calendarType);

    this.eventCategories = data?.map(
      (d: any) => new EventCategory(d.id, d.name, d.isActive)
    );
    this.setEditableCategories();
  };

  getEventImage = async (eventDetails: EventDetails = this.editableEvent) => {
    if (eventDetails.image.fileId) {
      let imageData = await this.fileService.getFile(eventDetails.image.fileId);
      eventDetails.image.setImageFromDownload(imageData);
    }
  };

  // Set observable villages for editableEvent
  getVillages = async (calendarName?: string) => {
    const villages = AuthUser.getInstance().Villages ?? [];

    // Multi-Village audiences (Independent Residents / Independent and
    // Serviced Apartment Residents) only apply to real villages whose
    // serviced-apartment-residents feature toggle matches that audience -
    // see `filterVillagesByServicedApartmentAudience`.
    const villagesForAudience = this.userService.isMultiVillageOptionSelected()
      ? filterVillagesByServicedApartmentAudience(villages, calendarName)
      : villages;

    runInAction(() => {
      this.multiSelectVillages = villagesForAudience.map(
        (village) => new KeyValue(village.id, village.name)
      );

      if (this.editableEvent.isEditMode) {
        // Set selected villages when editing an event.
        this.setEventSelectedVillages();
      }

      this.multiSelectVillages.unshift(allVillagesInNZ);
      this.multiSelectVillages.unshift(allVillagesInAU);
      this.multiSelectVillages.unshift(allVillageKeyValue);
      this.editableEvent.villages.setItems(this.multiSelectVillages);
    });
  };

  getVillageCode = (): string => {
    return this.userService.getSelectedVillage()?.code || "";
  };

  setEditableCategories = () => {
    this.editableEvent.category.setItems(
      this.eventCategories.map((ec) => new KeyValue(ec.id, ec.name))
    );
  };

  setEditableLocations = () => {
    this.editableEvent.location.additionalItems = [
      new KeyValue(
        VirtualEventConstant,
        translationProvider("event:labels.virtualEventSelect")
      ),
      new KeyValue(
        CustomLocationConstant,
        translationProvider("event:labels.customLocationSelect")
      )
    ];
    this.editableEvent.location.setItems(
      this.sharedLocations.map((sl) => new KeyValue(sl.id, sl.name))
    );

    if (this.editableEvent.isMultiVillage) {
      // Multi village event location must be set to virtual event.
      this.editableEvent.location.setSelectedItem(VirtualEventConstant);
      this.editableEvent.location.setIsDirty(false);
    }
  };

  setEventSelectedVillages = () => {
    // VillageIds null refers to 'All Village' event.
    if (
      this.editableEvent.villageIds === null ||
      this.editableEvent.villageIds === undefined
    ) {
      this.editableEvent.villages.setSelectedItems(this.multiSelectVillages);
      this.editableEvent.villages.setIsDirty(false);
      return;
    }

    const selectedVillageIds = this.editableEvent.villageIds!;
    const selectedVillageKeyValues = this.multiSelectVillages.filter((value) =>
      selectedVillageIds.find((villageId) => villageId === value.value)
    );
    this.editableEvent.villages.setSelectedItems(selectedVillageKeyValues);
    this.editableEvent.villages.setIsDirty(false);
  };

  setIsMultiVillageEvent = () => {
    this.editableEvent.isMultiVillage =
      this.userService.isMultiVillageOptionSelected();
  };
  loadEditableEvent = async (
    id: string,
    reset: boolean = true,
    villageId?: string
  ) => {
    if (reset) {
      runInAction(() => {
        this.resetEditableEvent();
      });
    }
    // A newer load or reset makes this one stale, so it must not overwrite editableEvent.
    const loadId = ++this.editableEventLoadId;
    const isSuperseded = () => loadId !== this.editableEventLoadId;

    let event = await this.eventService.getEvent(
      id,
      this.occurrenceDate,
      villageId
    );
    if (isSuperseded()) {
      return;
    }
    let eventRecurrenceDetails = new EventRecurrenceDetails(this.getTimeZone());

    if (event.recurrence) {
      eventRecurrenceDetails.frequency = event.recurrence.frequency;
      eventRecurrenceDetails.interval = event.recurrence.interval;
      eventRecurrenceDetails.recurrenceEndDate = new DateObservable(
        new Date(event.recurrence.endDate)
      );
      eventRecurrenceDetails.baseDate = event.recurrence.baseDate;
      eventRecurrenceDetails.setDaysWeeks(
        event.startDate,
        this.getTimeZone(),
        event.recurrence.weekdays?.map((d: any) =>
          recurrenceWeekdays.indexOf(d)
        ),
        event.recurrence.monthWeeks
      );
    }

    // Constructed in an action so the model's dirty-tracking reactions ignore constructor-time sets.
    const loadedEvent = runInAction(
      () =>
        new EventDetails(
          this.userService.getSelectedVillage()?.timeZone!,
          event.id.id,
          event.title,
          event.category.id,
          event.location.id,
          event.location.name,
          event.description,
          new Date(event.startDate),
          new Date(event.endDate),
          event.isAllDay ?? false,
          event.price,
          event.bookingDeadline,
          event.capacity,
          event.primaryImage === null ? null : event.primaryImage.fileId,
          event.featured,
          event.location.name,
          event.templateId,
          undefined,
          undefined,
          event.capacity,
          event.reservedCapacity,
          event.totalBookings,
          event.totalStaffAssistedBookings,
          event.capacityRemaining,
          event.reservedCapacityRemaining,
          event.hasPaidAttendees,
          event.totalPaidBookings,
          event.isMultiVillage,
          event.villageIds,
          event.category.name,
          event.isReleased,
          event.statusChangeReason,
          event.status,
          eventRecurrenceDetails,
          event.recurringStatus,
          event.baseEventId,
          event.highestBookingCountForSeries,
          this.occurrenceDate !== undefined,
          event.totalWaitlistedBookings,
          event.releaseDate ? new Date(event.releaseDate) : undefined,
          event.personTypes ? event.personTypes : undefined
        )
    );

    // Populate the image before swapping so the image picker is never blank after a reload.
    const previousImage = this.editableEvent.image;
    if (
      previousImage.file &&
      !previousImage.hasNewUpload &&
      previousImage.fileId === loadedEvent.image.fileId
    ) {
      loadedEvent.image.setImageFromDownload(previousImage.file);
    } else {
      await this.getEventImage(loadedEvent);
    }
    if (isSuperseded()) {
      return;
    }

    runInAction(() => {
      this.editableEvent = loadedEvent;
    });
    await this.getVillages();
    if (isSuperseded()) {
      return;
    }
    this.setEditableCategories();
    this.setEditableLocations();
    await Promise.all([
      this.loadEventRelease(),
      this.loadEventOccurrences(false, loadedEvent),
      this.loadEventOccurrences(true, loadedEvent)
    ]);
  };

  loadEventOccurrences = async (
    withBookingsOnly?: boolean,
    eventDetails: EventDetails = this.editableEvent
  ) => {
    if (!eventDetails.isRecurringEvent) {
      return;
    }

    if (withBookingsOnly && !EventDetailsHelper.isBookableEvent(eventDetails)) {
      // Clear booked event occurrence information if a non-bookable event
      runInAction(() => {
        this.bookedEventOccurrenceInfos = [];
      });
      return;
    }

    const eventOccurrenceQueryDate =
      eventDetails.eventDateDetails.startDate.date! < new Date()
        ? addDays(new Date(), -1).toISOString()
        : eventDetails.eventDateDetails.startDate.date!.toISOString();

    let eventOccurrences = await this.eventService.getEventOccurrenceInfos(
      eventDetails.id,
      eventOccurrenceQueryDate,
      withBookingsOnly
    );

    if (eventDetails !== this.editableEvent) {
      return;
    }

    const occurrenceInfos = eventOccurrences.map(
      (d: any) =>
        new EventOccurrenceInformation(
          new Date(d.occurrenceDate),
          d.totalBookings,
          d.totalPaidBookings
        )
    );
    runInAction(() => {
      if (withBookingsOnly) {
        this.bookedEventOccurrenceInfos = occurrenceInfos;
      } else {
        this.eventOccurrenceInfos = occurrenceInfos;
      }
    });
  };

  loadEventRelease = async () => {
    const schedule = await this.villageScheduleService.getMultiVillageSchedule(
      this.editableEvent.villageIds ?? []
    );
    const timeZone = this.getTimeZone();
    this.nextReleasePeriodStartDate = utcToZonedTime(
      new Date(schedule.calendarStartDate),
      timeZone
    );
  };

  resetEditableEvent = () => {
    this.editableEventLoadId++;
    runInAction(() => {
      this.editableEvent = new EventDetails(
        this.userService.getSelectedVillage()?.timeZone!
      );
    });
    this.setEditableCategories();
  };

  createEvent = async () => {
    await this.checkForNewImageAndUpload();

    this.editableEvent.selectedVillageId =
      this.userService.getSelectedVillage()?.id;
    return await this.eventService
      .saveEvent(this.editableEvent)
      .then((response: ResourceCreatedOrUpdated) => {
        this.resetEditableEvent();
        return response?.title;
      });
  };

  private async checkForNewImageAndUpload() {
    if (
      this.editableEvent.image.hasNewUpload &&
      this.editableEvent.image.file !== null
    ) {
      //upload image first
      await this.fileService
        .uploadFile(
          this.editableEvent.image.file,
          this.editableEvent.image.fileName
        )
        .then((response: FileCreated) => {
          this.editableEvent.image.setFileId(response?.fileId);
        });
    }
  }

  deleteEvent = async () => {
    return await this.eventService
      .deleteEvent(this.editableEvent, this.occurrenceDate)
      .then(() => {
        return this.editableEvent.title.text;
      });
  };

  deleteEventSeries = async () => {
    return await this.eventService
      .deleteEvent(this.editableEvent, undefined, this.occurrenceDate)
      .then(() => {
        return {
          eventTitle: this.editableEvent.title.text,
          occurrenceDate: this.occurrenceDate
        };
      });
  };

  cancelPostponeEvent = (eventStatus: EventStatus) => {
    this.editableEvent.eventStatus = eventStatus;
    // cancel reason must be reset to no change in order for page to be redirected
    // to newly detached cancelled event
    this.editableEvent.reason.setIsDirty(false);
    this.editableEvent.setIsDirty(false);

    return this.eventService.cancelPostponeEvent(
      this.editableEvent,
      this.eventUpdateOption,
      this.occurrenceDate
    );
  };

  editEvent = async () => {
    const currentDateTime = new Date();
    if (currentDateTime > this.editableEvent.eventDateDetails.endDate.date!) {
      this.resetEditableEvent();
      throw new Error("Event not saved", {cause: "Event end time elapsed"});
    }

    await this.checkForNewImageAndUpload();

    this.editableEvent.selectedVillageId =
      this.userService.getSelectedVillage()?.id;
    return await this.eventService
      .updateEvent(
        this.editableEvent,
        this.eventUpdateOption,
        this.occurrenceDate
      )
      .then(() => {
        const title = this.editableEvent.title.text;
        this.resetEditableEvent();
        return title;
      });
  };

  getEventBookings = async (
    eventId: string,
    status?: string
  ): Promise<void> => {
    runInAction(() => {
      this.resetEventBookings();
    });
    const villageId = this.userService.getSelectedVillage()?.id;
    const data = await this.eventService.getEventBookings(
      eventId,
      villageId!,
      this.occurrenceDate,
      status
    );

    const mapEventBookings = (d: EventBooking): EventBooking => {
      return new EventBooking(
        d.id,
        d.index,
        d.source,
        d.status,
        d.residentData,
        d.notes,
        d.personId,
        d.creatorId,
        d.isPaid,
        d.guestCount,
        this.editableEvent.isPassedEndDate,
        d.creatorName,
        d.concurrency,
        d.isPaidByFee
      );
    };
    runInAction(() => {
      this.confirmedEventBookingsList =
        data.confirmedBookings?.map(mapEventBookings);

      this.waitlistEventBookingsList =
        data.waitlistBookings?.map(mapEventBookings);

      this.cancelledEventBookingsList =
        data.cancelledBookings?.map(mapEventBookings);
    });
  };

  updateEventBooking = (eventBooking: EventBooking): Promise<any> => {
    return this.eventService.updateEventBooking(eventBooking);
  };

  updateMultipleEventBookings = (
    eventBookings: EventBooking[]
  ): Promise<any> => {
    return this.eventService.updateMultipleEventBookings(eventBookings);
  };

  createEventBooking = (
    eventId: string,
    eventBooking: EventBooking
  ): Promise<any> => {
    return this.eventService.createEventBooking(
      eventId,
      eventBooking,
      this.occurrenceDate
    );
  };

  resetEventBookings = () => {
    runInAction(() => {
      this.confirmedEventBookingsList = [];
      this.waitlistEventBookingsList = [];
      this.cancelledEventBookingsList = [];
    });
  };

  getTimeZone = (): string => {
    return this.userService.getSelectedVillage()?.timeZone!;
  };

  setOccurrenceDate = (occurrenceDate?: string | undefined | null) => {
    this.occurrenceDate = occurrenceDate ?? undefined;
  };

  setEventUpdateOption = (option?: UpdateRecurringEventOption) => {
    this.eventUpdateOption = option;
  };

  getNextReleasePeriodStartDate = (): Date | undefined => {
    return this.nextReleasePeriodStartDate;
  };

  getCurrentTime = (): Date => {
    return utcToZonedTime(new Date(), this.getTimeZone());
  };

  canMakeNonCosmeticChanges = (): boolean => {
    return this.eventOccurrenceInfos.some(
      (occurrenceInfo) =>
        occurrenceInfo.occurrenceDate > this.getNextReleasePeriodStartDate()!
    );
  };

  getCalendarByName = (calendarName: string): VillageCalendar | undefined => {
    return this.userService
      .getSelectedVillage()
      ?.calendars?.find((calendar) => calendar.name === calendarName);
  };

  loadCategorisedEvents = async (
    calendarType: EventCalendarType,
    startDate: Date,
    endDate: Date,
    villageId: string,
    personType: string
  ): Promise<void> => {
    const requestId = ++this.categorisedEventsRequestId;
    const data = await this.eventService.getCategorisedEvents(
      calendarType,
      startDate,
      endDate,
      villageId,
      personType
    );

    if (requestId === this.categorisedEventsRequestId) {
      this.categorisedEvents = data.eventCategories.map(
        (d: any) =>
          new EventCategory(
            d.id,
            d.name,
            d.isActive,
            d.events
              .filter((e: any) => new Date(e.startDate) >= startDate)
              .map((e: any) => this.mapEventToEventSummaryItem(e))
          )
      );
    }
  };

  invalidateCalendarRequests = () => {
    this.eventsListRequestId += 1;
    this.categorisedEventsRequestId += 1;
  };

  private mapEventToEventSummaryItem(event: any) {
    let eventRecurrenceDetails = new EventRecurrenceDetails(this.getTimeZone());

    if (event.recurrence) {
      eventRecurrenceDetails.frequency = event.recurrence.frequency;
      eventRecurrenceDetails.interval = event.recurrence.interval;
      eventRecurrenceDetails.recurrenceEndDate.setDate(
        new Date(event.recurrence.endDate)
      );
      eventRecurrenceDetails.baseDate = event.recurrence.baseDate;
      eventRecurrenceDetails.setDaysWeeks(
        event.startDate,
        this.getTimeZone(),
        event.recurrence.weekdays?.map((d: any) =>
          recurrenceWeekdays.indexOf(d)
        ),
        event.recurrence.monthWeeks
      );
    }

    return new EventSummaryItem(
      event.id.id,
      utcToZonedTime(new Date(event.startDate), this.getTimeZone()),
      utcToZonedTime(new Date(event.endDate), this.getTimeZone()),
      event.title,
      isWebLink(event.location.name)
        ? translationProvider("event:labels.virtualEventSelect")
        : event.location.name,
      this.getTimeZone(),
      event.isAllDay,
      !!event.bookingDeadline,
      event.capacityRemaining,
      event.reservedCapacityRemaining,
      event.featured,
      event.capacity,
      event.isMultiVillage,
      event.status,
      eventRecurrenceDetails,
      event.recurringStatus,
      event.startDate,
      event.releaseDate ? new Date(event.releaseDate) : undefined,
      event.description,
      event.category?.name,
      event.id.occurrenceDate,
      event.price,
      event.bookingDeadline,
      event.totalStaffAssistedBookings,
      event.totalBookings,
      event.totalWaitlistedBookings,
      event.totalCancelledBookings,
      event.statusChangeReason,
      event.reservedCapacity
    );
  }
}
